'use client';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, Copy, X } from 'lucide-react';
import Button from '@/components/ui/Button';
import { EMAIL } from '@/utils/constants';
import { TrackingEvent } from '@/utils/helpers';
import { analytics } from '@/lib/analytics';
import { copyToClipboard } from '@/utils/clipboard';
interface Props {
  trigger: ReactNode;
  message: string | ReactNode;
  title: string;
  emailMessage?: string;
  tracking?: TrackingEvent;
  pill?: string;
  closeButton?: boolean;
  /**
   * Extra action rendered below the email block. Can be a plain node,
   * or a render function that receives the modal's `close` callback so
   * the action can dismiss the modal itself (e.g. a "Try Again" button
   * that closes immediately and works in the background).
   */
  alternateButton?: ReactNode | ((close: () => void) => ReactNode);
}

export const EmailModal = ({
  trigger,
  message,
  title,
  emailMessage = 'Our email:',
  tracking,
  pill,
  closeButton = true,
  alternateButton,
}: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const handleCopy = async () => {
    try {
      await copyToClipboard(EMAIL);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const toggleModal = () => {
    setIsOpen(!isOpen);
    if (isOpen && tracking) {
      analytics.capture(tracking.event, tracking.properties);
    }
  };

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        toggleModal();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleOutsideClick);
    }

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  return (
    <>
      <button onClick={toggleModal} className={'text-white'}>
        {trigger}
      </button>

      {isOpen &&
        isMounted &&
        createPortal(
          <span
            className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-20"
            style={{ margin: 0 }}
          >
            <span
              ref={modalRef}
              className="bg-zinc-900 border border-zinc-500 border-opacity-50 text-white w-full max-w-md rounded-xl shadow-xl flex flex-col"
            >
              <span className="flex justify-between items-center p-4 border-zinc-700">
                <span className="text-xl font-semibold flex items-center">
                  {title}
                  {pill && (
                    <span className="text-black bg-white rounded-md px-2 text-sm uppercase ml-4 h-[26px] flex items-center justify-center font-mono">
                      {pill}
                    </span>
                  )}
                </span>
                <button onClick={toggleModal} className="text-gray-400 hover:text-white">
                  <X size={24} />
                </button>
              </span>
              <span className="p-4 flex flex-col gap-6">
                {typeof message === 'string' ? (
                  <span className="text-zinc-400">{message}</span>
                ) : (
                  message
                )}
                <span>
                  <span className="text-zinc-400">{emailMessage}</span>
                  <span className="flex justify-between items-center">
                    <span>{EMAIL}</span>
                    <button onClick={handleCopy} className={`flex items-center justify-center p-0`}>
                      {isCopied ? (
                        <>
                          <Check className="w-4 h-4 mr-1" color={'#00FF00'} />
                          <span style={{ color: '#00FF00' }}>Copied</span>
                        </>
                      ) : (
                        <>
                          <Copy className="w-4 h-4 mr-1" />
                          <span>Copy</span>
                        </>
                      )}
                    </button>
                  </span>
                </span>
                {closeButton && (
                  <Button
                    className={'w-full'}
                    variant={'slim'}
                    isPrimary={true}
                    onClick={toggleModal}
                  >
                    Close
                  </Button>
                )}
                {typeof alternateButton === 'function'
                  ? alternateButton(toggleModal)
                  : alternateButton}
              </span>
            </span>
          </span>,
          document.body
        )}
    </>
  );
};
